import { AcceptedHtmlElementContent, createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { ButtonStyle, CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsDialog, DialogPreConfig, showErrorInDialog } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { CjsGeneratedForm } from "@cervval.js/client-html5-ui/src/components/generatedForm/generatedForm";
import { GeneratedFormEditMode, GeneratedFormSaveMode } from "@cervval.js/client-html5-ui/src/components/generatedForm/generatedForm.types";
import { CjsSelectEditor } from "@cervval.js/client-html5-ui/src/editors/selectEditor/selectEditor";
import { Action } from "@cervval.js/common-action/src/action.model";
import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { getLogConsole, getLogService } from "@cervval.js/common/src/services/log.service";
import { ItemPropertyOrFunction, anyReallyAny, getItemPOFValue, objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { isPromise } from "@cervval.js/common/src/tools/promise.tools";
import { BaseElementTypes } from "@ehealth_tools/common/src/data/dto/base.dto";
import { getDataModel } from "./datamodel.tools";
import { i18nDialogTools } from "./dialog.tools.i18n";


interface CallActionDialogOptions<ElementTypes, Tablename extends keyof ElementTypes> {
    /** Callback called during action callback (eg. In encapsulateInAction(action => { .... })) */
    inActionCallback?: (newElement: ElementTypes[Tablename], action: Action<ElementTypes, unknown, unknown>) => void;
}


export interface CallCreateGeneratedFormDialogOptions<ElementTypes, TableName extends keyof ElementTypes> extends CallActionDialogOptions<ElementTypes, TableName> {
    /** Callback called after OK */
    callback?: (newElement: ElementTypes[TableName]) => void;
    /** Callback to specify the persistence (replace it in any case) */
    persistenceCallback?: (formElement: ElementTypes[TableName]) => Promise<void> | void;

    /** Autre option pour ajouter un bouton au dialogue */
    otherOption?: {
        title: string,
        callback: () => void
    };
    disable?: boolean;
    /** Default values to set in generated form */
    defaultValues?: Partial<{ [Field in keyof ElementTypes[TableName]]: ((generatedForm: CjsGeneratedForm<ElementTypes[TableName]>) => { value: ElementTypes[TableName][Field], disabled?: boolean }) | { value: ElementTypes[TableName][Field], disabled?: boolean } }>;
    /** Update functions when value changed */
    updateFunction?: Partial<{ [Field in keyof ElementTypes[TableName]]: (value: ElementTypes[TableName][Field], generatedForm: CjsGeneratedForm<ElementTypes[TableName]>) => void }>;
    /** Alternative PC depending of values */
    alternativePropertyConfig?: Partial<{ [Field in keyof ElementTypes[TableName]]: (value: anyReallyAny) => PropertyConfig[] | undefined }>;
}

/**
 * Build a dialog containing a generated form to create a specific element of provided table.
 * @template ElementTypes Record object with a table name as string, and the associated DTO object as value. It must extends type BaseElementTypes
 * @template TableName Key of ElementTypes, representing the table in which we would like to insert a new element
 * @param params Parameter objects
 * @param params.dialogTitle Title of the dialog
 * @param params.tablename Name of the table in which we wish to insert an element
 * @param params.propertyConfigs List of propertyConfig for the form
 * @param params.options Optional. Option object for the generated form
 * @param params.dialogCustomClassList Optional. List of class to append to the dialog HTML element
 */
export function callCreateGeneratedFormDialog<ElementTypes extends BaseElementTypes, TableName extends keyof ElementTypes & string>(params: {
    dialogTitle: string,
    tablename: TableName,
    propertyConfigs: PropertyConfig[],
    options?: CallCreateGeneratedFormDialogOptions<ElementTypes, TableName>,
    dialogCustomClassList?: string[]
}): void {

    const { dialogTitle, tablename, propertyConfigs, options, dialogCustomClassList } = params;

    const generatedForm = new CjsGeneratedForm<ElementTypes[TableName]>({
        formMode: {
            editMode: GeneratedFormEditMode.CREATE,
            saveMode: GeneratedFormSaveMode.STATIC
        }
    });


    /** ddefault value fields */
    if (options?.defaultValues) {
        for (const uid of objectKeys(options.defaultValues)) {
            const pc = propertyConfigs.find(v => v.propertyName === uid);
            if (pc) {
                const defaultValue = options.defaultValues[uid];
                if (defaultValue != null) {
                    const defaultValueVOF: { value: anyReallyAny, disabled?: boolean } = typeof defaultValue === "function" ? defaultValue(generatedForm) : defaultValue;
                    pc.options = { ...pc.options, defaultValue: () => defaultValueVOF.value, disabled: defaultValueVOF.disabled };
                }
            }
        }
    }
    const i18n = i18nDialogTools();
    const dialog = new CjsDialog({
        title: dialogTitle,
        footerButtons: [{
            button: {
                label: i18n.dialog.validate,
                cssStyle: ButtonStyle.PRIMARY,
                disabled: true
            },
            action: "confirm",
            callback: async () => {
                try {
                    let elementToCreate: ElementTypes[TableName];
                    if (options?.persistenceCallback) {
                        const result = generatedForm.save(false);
                        elementToCreate = result?.elementsToCreate?.[0];
                        if (elementToCreate) {
                            elementToCreate.__tableName = tablename;
                            const pCallbackResult = options.persistenceCallback(elementToCreate);
                            if (isPromise(pCallbackResult)) {
                                await pCallbackResult;
                            }
                        }
                        if (elementToCreate == null) {
                            throw new Error("Saving through custom persistence callback attempt failed : nothing to save ");

                        }
                        options?.callback?.(elementToCreate);
                    } else {
                        generatedForm.dataModelValueSaveEvent.on(() => {
                            setTimeout(() => {
                                if (elementToCreate == null) {
                                    throw new Error("Saving attempt failed : nothing to save ");

                                }
                                options?.callback?.(elementToCreate);
                            }, 0);
                        }, { once: true });
                        const result = generatedForm.save();
                        elementToCreate = result?.elementsToCreate?.[0];

                    }

                    dialog.hide();
                } catch (e) {
                    getLogConsole().error(e);
                }

            }
        }, {
            button: {
                label: i18n.dialog.cancel,
                cssStyle: ButtonStyle.SECONDARY
            },
            action: "close",
            callback: () => dialog.hide()
        }]
    });
    if (dialogCustomClassList != null) {
        try {
            dialog.classList.add(...dialogCustomClassList);
        } catch (err) {
            getLogService().error("Error when adding custom classes on dialog. Skipping custom classes addition");
        }
    }


    // call update function 
    const updateFct = options?.updateFunction;
    if (updateFct) {
        generatedForm.editorValueChangeEvent.on((e) => {
            for (const propertyName in updateFct) {
                if (e.detail.propertyConfig.propertyName === propertyName) {
                    const fct = updateFct[propertyName];
                    if (fct) {
                        fct(e.detail.value as anyReallyAny, generatedForm);
                    }
                }
            }
        });
    }

    const alternativePC = options?.alternativePropertyConfig;
    if (alternativePC) {
        generatedForm.editorValueChangeEvent.on((e) => {
            for (const propertyName in alternativePC) {
                if (e.detail.propertyConfig.propertyName === propertyName) {
                    const PC = alternativePC[propertyName]?.(e.detail.value);
                    if (PC) {
                        const result = generatedForm.save();
                        generatedForm.initialize(PC, dm, result.elementsToCreate);
                    }
                    break;
                }
            }
        });
    }

    dialog.addToBody(generatedForm);
    const dm = getDataModel(tablename, { inActionCallback: { onCreate: options?.inActionCallback } });
    const editedDataList: ElementTypes[TableName][] = [];
    if (options?.defaultValues) {
        editedDataList.push({ ...options?.defaultValues as anyReallyAny });
    }
    generatedForm.initialize(propertyConfigs, dm, editedDataList);
    // crado, allows selected data checking in form and persistance of removal (can't wait for GF refact at this point)
    const allFormItems = generatedForm.getFormItems();
    for (const property in allFormItems) {
        const editor = allFormItems[property].editor;
        if (editor instanceof CjsSelectEditor && editor.smartSelectOptions.multiSelect?.enable) {
            editor.smartSelectOptions.setAndRefreshIfChanged({
                callbacksToOverride: {
                    getItemValue: (item) => item.item.id,
                    getItemByValue: (items, value) => items.find(item => item.item.id === value)
                }
            });
        }
    }
    if (dialog.confirmButton != null) {
        linkConfirmButtonToFormValidate(generatedForm, dialog.confirmButton);
    }

    if (options?.otherOption) {
        const otherButton = new CjsButton({
            cssStyle: ButtonStyle.PRIMARY,
            label: options?.otherOption.title
        });
        otherButton.clickEvent.on((e) => {
            options.otherOption?.callback();
            dialog.hide();
        });
        dialog.addToFooter(otherButton);
    }
}

/** Button enabled only if form validated */
// ! Call this function on an already initialized form
export function linkConfirmButtonToFormValidate(form: CjsGeneratedForm<anyReallyAny>, button: CjsButton): void {
    form.editorValueChangeEvent.on((e) => {
        button.options.setAndRefreshIfChanged({ disabled: !form.validateForm() });
    });
}


interface CallDeleteDialogOptions<ElementTypes, Tablename extends keyof ElementTypes> extends CallActionDialogOptions<ElementTypes, Tablename> {
    callback?: () => void;
}

/** 
 * Function to create a generic dialog for DTO deletion
 * @param params Parameters object
 * @param params.dialogTitle Title of the dialog
 * @param params.tableName Name of the table from which elements should be removed
 * @param params.dialogHeaderContent Optional. Object to define the dialog header text. All options are mutually exclusive
 * @param params.dialogHeaderContent.innerHTML HTML string for the header of dialog element
 * @param params.dialogHeaderContent.labelField Attribute of the DTO, that will be used as display in dialog on a template string
 * @param params.dtos List of DTO object to remove
 * @param params.options  Options object
 * @param params.customDialogCssClass Optional. CSS class to add on created dialog
 */
export function callDeleteDialog<
    ElementTypes extends BaseElementTypes,
    TableName extends keyof ElementTypes & string = keyof ElementTypes & string
>(
    params: {
        dialogTitle: string,
        tableName: TableName,
        dialogHeaderContent?: {
            innerHTML?: string,
            labelField?: ItemPropertyOrFunction<ElementTypes[TableName], string>
        },
        dtos: ElementTypes[TableName][],
        options?: CallDeleteDialogOptions<ElementTypes, TableName>,
        customDialogCssClass?: string
    }
): void {

    const { dialogTitle, tableName, dialogHeaderContent, dtos, options, customDialogCssClass } = params;
    const i18n = i18nDialogTools();

    const dm = getDataModel(tableName, { inActionCallback: { onDelete: options?.inActionCallback } });

    let dialogHeaderHtml = "";
    if (dialogHeaderContent != null && dialogHeaderContent.innerHTML != null) {
        dialogHeaderHtml = dialogHeaderContent.innerHTML;
    } else if (dialogHeaderContent != null && dialogHeaderContent.labelField != null) {
        dialogHeaderHtml = i18n.deleteDialogHeaderWithLabelFieldHtmlTemplate(
            dtos.map(dto => getItemPOFValue(dto, dialogHeaderContent.labelField!)).join(", ")
        );
    } else {
        dialogHeaderHtml = i18n.deleteDialogHeaderHtmlTemplate(dtos.length);
    }

    // TODO Add more complex display of impacted elements  ?

    const dialogBodyContent = createHtmlElement("div", {
        innerHTML: [dialogHeaderHtml]
    });

    callContinueDialog({
        dialogTitle: dialogTitle, preConfig: DialogPreConfig.DELETE,
        message: dialogBodyContent,
        callback: () => {
            dm.onDelete(dtos).then(_ => {
                options?.callback?.();
            }).catch(showErrorInDialog);
        },
        customDialogCssClass: customDialogCssClass
    });
}

/**
 * Create a `CjsDialog` element with provided parameters
 * @param params Parameters object
 * @param params.dialogTitle Title of the dialog
 * @param params.preConfig `DialogPreConfig` value
 * @param params.message Dialog body, as an `AcceptedHtmlElementContent`
 * @param params.callback Optional. Callback to call when confirm button is clicked
 * @param params.customDialogCssClass Optional. CSS class to add on created `CjsDialog`
 */
export function callContinueDialog(
    params: {
        dialogTitle: string,
        preConfig: DialogPreConfig,
        message: AcceptedHtmlElementContent,
        callback?: () => void,
        customDialogCssClass?: string
    }
): void {

    const { dialogTitle, preConfig, message, callback, customDialogCssClass } = params;
    const dialog = new CjsDialog({
        title: dialogTitle,
        preConfig,
        onConfirmButtonClick: () => {
            try {
                callback?.();
            } catch (e) {
                getLogConsole().error(e);
                throw new Error(e as string);
            }
            return Promise.resolve();
        }
    });

    if (customDialogCssClass != null) {
        try {
            dialog.classList.add(customDialogCssClass);
        } catch (err) {
            getLogService().warn("[callContinueDialog]", "Error in css syntax, can't add provided css class", customDialogCssClass);
        }

    }

    dialog.addToBody(message);
}